/* ------------------------------------------------------------------
  File: BaseCard.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable base card wrapper for the Accend Holdings website.
//   - Defines consistent card structure, spacing, surface, border, radius,
//     shadow, hover behavior, and full-height behavior.
//   - Uses the acc-base-card namespace to avoid conflicts with Bootstrap.
//   - Serves as the foundation for specialized cards such as InfoCard,
//     LinkCard, StatCard, ServiceCard, SectorCard, and SubsidiaryCard.

/**
 * BaseCard
 *
 * A reusable card wrapper component for consistent card styling across
 * the Accend Holdings frontend.
 *
 * @param {React.ReactNode} children
 * The card content.
 *
 * @param {React.ElementType} [as="div"]
 * The HTML element or React component used to render the card.
 *
 * @param {"default" | "muted" | "soft" | "inverse"} [variant="default"]
 * Controls the card surface style.
 *
 * @param {"none" | "sm" | "md" | "lg"} [padding="md"]
 * Controls internal card spacing.
 *
 * @param {boolean} [hoverable=false]
 * Enables hover elevation and movement.
 *
 * @param {boolean} [bordered=true]
 * Controls whether the card has a border.
 *
 * @param {boolean} [fullHeight=false]
 * Makes the card stretch to the full height of its parent.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 */

import "./BaseCard.css";

const BaseCard = ({
  children,
  as: Component = "div",
  variant = "default",
  padding = "md",
  hoverable = false,
  bordered = true,
  fullHeight = false,
  className = "",
  ...props
}) => {
  const baseCardClasses = [
    "acc-base-card",
    `acc-base-card--${variant}`,
    `acc-base-card--padding-${padding}`,
    hoverable ? "acc-base-card--hoverable" : "",
    bordered ? "acc-base-card--bordered" : "acc-base-card--no-border",
    fullHeight ? "acc-base-card--full-height" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Component className={baseCardClasses} {...props}>
      {children}
    </Component>
  );
};

export default BaseCard;